import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Copy, Check, Sparkles, FileText, Download, ChevronLeft, Plus, Trash2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useMasterResumes } from "@/hooks/useMasterResumes";
import { jsPDF } from "jspdf";

export const CoverLetterGenerator = ({ 
  onBack,
  initialLetterId,
  onLetterSelected
}: { 
  onBack?: () => void;
  initialLetterId?: string | null;
  onLetterSelected?: (id?: string) => void;
}) => {
  const { user } = useAuth();
  const { profile, refetchProfile } = useProfile();
  const { resumes: masterResumes } = useMasterResumes();
  const [selectedCvId, setSelectedCvId] = useState<string | null>(null);
  const [currentLetterId, setCurrentLetterId] = useState<string | null>(null);

  const [mode, setMode] = useState<"dashboard" | "generator">("dashboard");
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [cv, setCv] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [hiringManager, setHiringManager] = useState("");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [showInputs, setShowInputs] = useState(true);

  const [copied, setCopied] = useState(false);

  // AI Chat Assistant States
  const [chatLoading, setChatLoading] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState<Array<{ role: "assistant" | "user"; content: string }>>([
    {
      role: "assistant",
      content: "Welcome to AI Edit! Tell me how you'd like your text transformed — try: 'Fix grammar', 'Make it more concise', or 'Rewrite for clarity'. Or choose a quick preset below!"
    }
  ]);
  const [showChatPanel, setShowChatPanel] = useState(true);

  // Load history
  useEffect(() => {
    const loadHistory = async () => {
      if (!user) return;
      setHistoryLoading(true);
      const { data, error } = await supabase
        .from("cover_letters")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      
      if (data) {
        setHistory(data);
      }
      setHistoryLoading(false);
    };
    
    if (user && mode === "dashboard") {
      loadHistory();
    }
  }, [user, mode]);

  // Debounced cloud synchronization for cover letter edits
  useEffect(() => {
    if (!currentLetterId || !result) return;

    const timer = setTimeout(async () => {
      const { error } = await supabase
        .from("cover_letters")
        .update({ generated_letter: result })
        .eq("id", currentLetterId);

      if (error) {
        console.error("Failed to sync edited cover letter to cloud:", error);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [result, currentLetterId]);

  // Load initial cover letter if ID is passed in the URL (for deep links/refresh)
  useEffect(() => {
    const loadInitialLetter = async () => {
      if (!initialLetterId || !user) return;

      setLoading(true);
      const { data, error } = await supabase
        .from("cover_letters")
        .select("*")
        .eq("id", initialLetterId)
        .single();

      if (data) {
        setCv(data.cv_input || "");
        setJobDescription(data.job_description || "");
        setCompanyName(data.company_name || "");
        setHiringManager(data.hiring_manager || "");
        setResult(data.generated_letter || null);
        setCurrentLetterId(data.id);

        // Find matching base CV from user library
        const matching = masterResumes.find(r => r.content === data.cv_input);
        setSelectedCvId(matching ? matching.id : null);

        setShowInputs(false);
        setMode("generator");
      } else {
        console.error("Failed to load cover letter:", error);
        toast.error("Cover letter not found.");
      }
      setLoading(false);
    };

    loadInitialLetter();
  }, [initialLetterId, user, masterResumes]);

  const handleCreateNew = () => {
    if (masterResumes && masterResumes.length > 0) {
      setCv(masterResumes[0].content);
      setSelectedCvId(masterResumes[0].id);
    } else {
      setCv("");
      setSelectedCvId(null);
    }
    setJobDescription("");
    setCompanyName("");
    setHiringManager("");
    setResult(null);
    setCurrentLetterId(null);
    onLetterSelected?.(undefined);
    setShowInputs(true);
    setMode("generator");
  };

  const handleLoadExisting = (item: any) => {
    setCv(item.cv_input || "");
    setJobDescription(item.job_description || "");
    setCompanyName(item.company_name || "");
    setHiringManager(item.hiring_manager || "");
    setResult(item.generated_letter || null);
    setCurrentLetterId(item.id);
    onLetterSelected?.(item.id);
    
    // Find matching base CV from user library
    const matching = masterResumes.find(r => r.content === item.cv_input);
    setSelectedCvId(matching ? matching.id : null);
    
    setShowInputs(false);
    setMode("generator");
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("cover_letters").delete().eq("id", id);
    if (!error) {
      setHistory(prev => prev.filter(h => h.id !== id));
      toast.success("Cover letter deleted");
    } else {
      toast.error("Failed to delete cover letter");
    }
  };

  const handleGenerate = async () => {
    if (!cv) {
      toast.error("Please paste your CV or experience.");
      return;
    }
    if (!jobDescription) {
      toast.error("Please paste the job description.");
      return;
    }

    if (!user) {
      toast.error("You must be logged in to use this feature.");
      return;
    }

    if (profile && profile.credits_used >= profile.credits_total) {
      toast.error("You've used all your credits. Please upgrade your plan.");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('generate-cover-letter', {
        body: {
          cv,
          jobDescription,
          companyName,
          hiringManager,
        }
      });

      if (error) {
        throw new Error(error.message || "Failed to generate cover letter.");
      }

      if (!data || !data.letter) {
        throw new Error("Invalid response from the server.");
      }

      setResult(data.letter);
      setShowInputs(true); // Keep split screen after generating new
      toast.success("Cover letter generated successfully!");

      // Save to database
      const { data: insertedData, error: insertError } = await supabase
        .from("cover_letters")
        .insert({
          user_id: user.id,
          cv_input: cv,
          job_description: jobDescription,
          company_name: companyName,
          hiring_manager: hiringManager,
          generated_letter: data.letter,
        })
        .select("id")
        .single();

      if (insertError) {
        console.error("Failed to save cover letter to database:", insertError);
        toast.error(`Warning: Cover letter generated but failed to save (${insertError.message})`);
      } else if (insertedData) {
        setCurrentLetterId(insertedData.id);
        onLetterSelected?.(insertedData.id);
      }

      refetchProfile();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!result) return;
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadAsPdf = () => {
    if (!result) return;
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxLineWidth = pageWidth - (margin * 2);
    
    doc.setFont("helvetica", "normal");
    const rawLines = result.split("\n");
    let currentY = margin;
    const lineHeight = 6.5;
    
    for (let i = 0; i < rawLines.length; i++) {
      const lineText = rawLines[i].trim();
      
      if (lineText === "") {
        currentY += 4;
        continue;
      }
      
      // If the first non-empty line exists, render candidate name in bold and larger font!
      if (i === 0 && rawLines[0].trim().length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.text(lineText, margin, currentY);
        doc.setFont("helvetica", "normal");
        currentY += 10;
        continue;
      }
      
      doc.setFontSize(11);
      doc.setTextColor(30, 41, 59);
      
      const wrappedLines = doc.splitTextToSize(lineText, maxLineWidth);
      
      if (currentY + (wrappedLines.length * lineHeight) > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
      }
      
      doc.text(wrappedLines, margin, currentY);
      currentY += wrappedLines.length * lineHeight;
    }
    
    const filename = companyName 
      ? `Cover_Letter_${companyName.replace(/\s+/g, "_")}.pdf` 
      : "Cover_Letter.pdf";
    doc.save(filename);
    toast.success("PDF cover letter downloaded successfully!");
  };

  const handleChatSubmit = async (instruction: string) => {
    if (!instruction.trim() || !result || chatLoading) return;
    
    // Add user message
    const updatedHistory = [...chatHistory, { role: "user" as const, content: instruction }];
    setChatHistory(updatedHistory);
    setChatInput("");
    setChatLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-cover-letter', {
        body: {
          cv,
          jobDescription,
          companyName,
          hiringManager,
          currentLetter: result,
          instructions: instruction,
        }
      });
      
      if (error) {
        throw new Error(error.message || "Failed to edit cover letter.");
      }
      
      if (!data || !data.letter) {
        throw new Error("Invalid response from server.");
      }
      
      setResult(data.letter);
      setChatHistory([
        ...updatedHistory,
        {
          role: "assistant" as const,
          content: `I've successfully updated your cover letter based on your request: "${instruction}".`
        }
      ]);
      toast.success("Cover letter refined successfully!");
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to edit cover letter.");
      setChatHistory([
        ...updatedHistory,
        {
          role: "assistant" as const,
          content: `Sorry, I encountered an error: ${err.message || "Failed to edit cover letter."}. Please try again.`
        }
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  if (mode === "dashboard") {
    return (
      <div className="flex-1 flex flex-col min-w-0 bg-background transition-colors overflow-hidden">
        <header className="h-16 border-b border-border bg-card/30 flex items-center justify-between px-8 flex-shrink-0 transition-colors">
          <div className="flex items-center gap-4">
            {onBack && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  onLetterSelected?.(undefined);
                  onBack();
                }}
                className="text-muted-foreground hover:text-foreground -ml-4 mr-2"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-foreground">Cover Letter</h2>
          </div>
          <Button 
            onClick={handleCreateNew}
            className="bg-primary hover:bg-primary/90 text-black text-xs font-bold py-1.5 h-9 px-4 flex items-center gap-2 rounded-lg shadow-lg shadow-primary/20"
          >
            <Plus className="w-4 h-4" /> Create Cover Letter
          </Button>
        </header>
        
        <div className="flex-1 p-8 overflow-y-auto custom-scrollbar bg-background transition-colors">
          <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-xl dark:shadow-2xl dashboard-table-container">
            <table className="w-full text-left border-collapse cv-history-table">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Company</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Date</th>
                  <th className="px-6 py-4 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {historyLoading ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-20 text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto" />
                    </td>
                  </tr>
                ) : history.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-20 text-center">
                      <div className="opacity-20 mb-3 text-foreground"><FileText className="w-12 h-12 mx-auto" /></div>
                      <p className="text-muted-foreground text-sm">No cover letters yet. Click "Create Cover Letter" to start.</p>
                    </td>
                  </tr>
                ) : (
                  history.map((h: any) => (
                    <tr key={h.id} className="group hover:bg-muted/40 transition-all border-t border-border/10 cursor-pointer" onClick={() => handleLoadExisting(h)}>
                      <td className="px-6 py-4" data-label="Company">
                        <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                          {h.company_name || "General Cover Letter"}
                        </p>
                      </td>
                      <td className="px-6 py-4" data-label="Date">
                        <p className="text-[11px] text-muted-foreground/60">{new Date(h.created_at).toLocaleDateString("en-GB")}</p>
                      </td>
                      <td className="px-6 py-4 text-right" data-label="Action">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={(e) => { e.stopPropagation(); handleLoadExisting(h); }}
                            className="h-8 text-[10px] font-bold uppercase tracking-wider text-primary hover:text-primary/80 hover:bg-primary/5"
                          >
                            View
                          </Button>
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleDelete(h.id); }}
                            className="p-2 transition-all hover:scale-110 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-400"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-12 custom-scrollbar bg-background/50 h-full flex flex-col min-w-0">
      <div className="max-w-6xl mx-auto space-y-8 w-full">
        <div className="flex items-center justify-between flex-shrink-0">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setMode("dashboard");
              onLetterSelected?.(undefined);
            }}
            className="text-muted-foreground hover:text-foreground -ml-2"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Cover Letters
          </Button>

          {result && !loading && !showChatPanel && (
            <Button
              onClick={() => setShowChatPanel(true)}
              className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5 animate-bounce"
            >
              <Sparkles className="w-3.5 h-3.5" /> ✨ Open AI Edit Panel
            </Button>
          )}
        </div>

        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
            <FileText className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Cover Letter Generator</h2>
            <p className="text-muted-foreground text-sm mt-1">Generate a highly tailored cover letter based on your CV and the job description.</p>
          </div>
        </div>

        {result && !loading ? (
          /* Premium Editor Grid with AI Edit Chat panel */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Left/Middle Column (Cover Letter Editor) */}
            <div className={`bg-card rounded-2xl border border-border shadow-lg p-6 flex flex-col h-full min-h-[600px] ${showChatPanel ? 'lg:col-span-2' : 'lg:col-span-3'} transition-all duration-300`}>
              <div className="flex flex-col h-full space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-lg text-foreground">Your Cover Letter</h3>
                    <span className="text-[9px] font-bold text-primary uppercase tracking-widest bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full animate-pulse">
                      Editable
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={downloadAsPdf}
                      className="h-8 text-xs border-border hover:bg-muted gap-1.5"
                    >
                      <Download className="w-3.5 h-3.5" />
                      ⬇️ Download PDF
                    </Button>
                    <Button
                      variant="outline"
                      onClick={copyToClipboard}
                      className="h-8 text-xs bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 gap-1.5"
                    >
                      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied ? "Copied" : "📋 Copy to Clipboard"}
                    </Button>
                  </div>
                </div>

                <div className="flex-1 pr-2">
                  <textarea
                    value={result}
                    onChange={(e) => setResult(e.target.value)}
                    className="w-full h-full min-h-[500px] bg-muted/30 border border-border rounded-xl p-6 text-sm text-foreground leading-relaxed focus:outline-none focus:border-primary/50 resize-none font-sans custom-scrollbar"
                    placeholder="Your cover letter will appear here..."
                  />
                </div>
              </div>
            </div>

            {/* Right Column (AI Edit Panel) */}
            {showChatPanel && (
              <div className="bg-card rounded-2xl border border-border shadow-lg p-6 flex flex-col h-full min-h-[600px] lg:col-span-1 transition-all duration-300 relative">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-border/80 pb-4 mb-4 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <Sparkles className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span className="font-bold text-sm text-foreground uppercase tracking-wider">AI Edit</span>
                    <span className="text-[8px] font-bold text-black bg-primary px-1.5 py-0.5 rounded-md uppercase tracking-widest font-mono">BETA</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowChatPanel(false)}
                    className="h-7 w-7 p-0 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {/* Message Log */}
                <div className="flex-1 overflow-y-auto space-y-4 pr-1 mb-4 custom-scrollbar min-h-[250px] max-h-[300px]">
                  {chatHistory.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex flex-col max-w-[85%] ${msg.role === 'user' ? 'ml-auto items-end' : 'mr-auto items-start'}`}
                    >
                      <div
                        className={`rounded-2xl px-4 py-3 text-xs leading-relaxed ${
                          msg.role === 'user'
                            ? 'bg-primary text-black font-semibold rounded-tr-none'
                            : 'bg-muted/40 border border-border/60 text-foreground rounded-tl-none'
                        }`}
                      >
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex flex-col max-w-[85%] mr-auto items-start">
                      <div className="rounded-2xl px-4 py-3 bg-muted/40 border border-border/60 text-foreground rounded-tl-none flex items-center gap-2 text-xs">
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                        Refining cover letter...
                      </div>
                    </div>
                  )}
                </div>

                {/* Presets Chips */}
                <div className="flex-shrink-0 space-y-2 border-t border-border/40 pt-4 mb-4">
                  <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">AI Presets</span>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { label: "👔 Make more formal", query: "Make the tone of my cover letter more formal and professional." },
                      { label: "⚡ Make it shorter", query: "Make this cover letter more concise and direct." },
                      { label: "📝 Translate to Spanish", query: "Translate this entire cover letter to Spanish." },
                      { label: "✨ Make more catchy", query: "Make the cover letter more engaging, creative, and catchy." },
                      { label: "🛠️ Technical role", query: "Tailor this cover letter to emphasize high technical expertise and systems engineering skills." }
                    ].map((preset, idx) => (
                      <button
                        key={idx}
                        type="button"
                        disabled={chatLoading || !result}
                        onClick={() => handleChatSubmit(preset.query)}
                        className="text-[10px] font-semibold px-2.5 py-1 rounded-full border border-border bg-muted/30 hover:border-primary/30 hover:bg-primary/5 text-muted-foreground hover:text-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed text-left"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Submit Form */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleChatSubmit(chatInput);
                  }}
                  className="flex-shrink-0 relative border border-border rounded-xl bg-muted/30 focus-within:border-primary/50 transition-colors p-2 flex items-center gap-2"
                >
                  <textarea
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Suggest changes or improvements here..."
                    className="flex-1 bg-transparent text-xs text-foreground focus:outline-none placeholder-muted-foreground resize-none h-10 py-1 pl-2 custom-scrollbar font-sans"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleChatSubmit(chatInput);
                      }
                    }}
                  />
                  <Button
                    type="submit"
                    disabled={chatLoading || !chatInput.trim()}
                    className="w-8 h-8 rounded-lg bg-primary hover:bg-primary/90 text-black flex items-center justify-center p-0 flex-shrink-0 disabled:opacity-50"
                  >
                    {chatLoading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                  </Button>
                </form>
              </div>
            )}
          </div>
        ) : (
          /* Normal Inputs & Skeleton Layout */
          <div className={`grid ${showInputs ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-8`}>
            {/* Left Column - Input */}
            {showInputs && (
              <div className="space-y-6 flex flex-col h-full">
                <div className="bg-card rounded-2xl border border-border shadow-lg p-6 flex-1 space-y-5 flex flex-col">
                  
                  <div className="space-y-3 flex-shrink-0">
                    <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest block ml-1">
                      Step 1: Your Base CV
                    </label>

                    {selectedCvId ? (
                      <div className="bg-card rounded-xl p-5 border border-border shadow-lg relative group transition-all hover:border-primary/30 text-left">
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              setCv("");
                              setSelectedCvId(null);
                            }} 
                            className="text-[10px] h-7 text-muted-foreground hover:text-red-400"
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Change
                          </Button>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-bold text-sm text-foreground truncate max-w-[280px]">
                              Using: {masterResumes.find(r => r.id === selectedCvId)?.name || "Selected CV"}
                            </h3>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-[0.1em] font-bold">Ready for cover letter tailoring</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {masterResumes && masterResumes.length > 0 && (
                          <div className="grid grid-cols-1 gap-2 text-left animate-in fade-in slide-in-from-bottom-2 duration-300">
                            {masterResumes.map((resume) => (
                              <button
                                key={resume.id}
                                type="button"
                                onClick={() => {
                                  setCv(resume.content);
                                  setSelectedCvId(resume.id);
                                  toast.success(`Loaded base CV: ${resume.name}`);
                                }}
                                className="flex items-center justify-between p-4 bg-card border border-border rounded-xl transition-all group hover:border-primary/50 hover:bg-primary/5"
                              >
                                <div className="flex items-center gap-3 text-left">
                                  <div className="p-2 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors">
                                    <FileText className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
                                  </div>
                                  <span className="text-sm font-semibold block">{resume.name}</span>
                                </div>
                                <Plus className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-all" />
                              </button>
                            ))}
                          </div>
                        )}

                        {masterResumes && masterResumes.length > 0 && !cv && (
                          <div className="flex items-center gap-4 py-1">
                            <div className="h-px bg-border flex-1" />
                            <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest">or</span>
                            <div className="h-px bg-border flex-1" />
                          </div>
                        )}

                        {(!masterResumes || masterResumes.length === 0 || !selectedCvId) && (
                          <textarea
                            value={cv}
                            onChange={(e) => setCv(e.target.value)}
                            placeholder="Or paste your CV text manually / summarise key experience"
                            className="w-full h-32 bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary/50 text-foreground resize-none"
                          />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 flex-1 flex flex-col min-h-[150px]">
                    <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest ml-1">Paste the Job Description</label>
                    <textarea
                      value={jobDescription}
                      onChange={(e) => setJobDescription(e.target.value)}
                      placeholder="Copy and paste the full job description here"
                      className="w-full flex-1 bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary/50 text-foreground resize-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest ml-1">Company Name</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="e.g. Google, Deloitte, Spotify"
                      className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary/50 text-foreground"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest ml-1">Hiring Manager Name (optional)</label>
                    <input
                      type="text"
                      value={hiringManager}
                      onChange={(e) => setHiringManager(e.target.value)}
                      placeholder="e.g. Sarah Johnson — leave blank if unknown"
                      className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary/50 text-foreground"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary/90 text-black font-bold py-7 rounded-2xl shadow-xl shadow-primary/20 transition-all hover:scale-[1.01] active:scale-95 group"
                >
                  <div className="flex items-center gap-2">
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Sparkles className="w-5 h-5 transition-transform group-hover:rotate-12" />
                    )}
                    <span className="uppercase tracking-widest text-sm">
                      {loading ? "Generating..." : "✨ Generate Cover Letter"}
                    </span>
                  </div>
                </Button>
              </div>
            )}

            {/* Right Column - Skeleton / Empty Output */}
            <div className="bg-card rounded-2xl border border-border shadow-lg p-6 flex flex-col h-full min-h-[600px] relative">
              {!loading && !result && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                  <div className="w-16 h-16 rounded-full bg-primary/5 flex items-center justify-center mb-4">
                    <FileText className="w-8 h-8 text-primary/40" />
                  </div>
                  <p className="text-muted-foreground uppercase tracking-widest font-bold text-sm">
                    Your cover letter will appear here
                  </p>
                </div>
              )}

              {loading && (
                <div className="space-y-6 animate-pulse">
                  <div className="space-y-3">
                    <div className="h-4 w-1/4 bg-muted rounded"></div>
                    <div className="h-6 w-full bg-muted rounded-xl"></div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-4 w-1/4 bg-muted rounded"></div>
                    <div className="h-32 w-full bg-muted rounded-xl"></div>
                    <div className="h-32 w-full bg-muted rounded-xl"></div>
                    <div className="h-16 w-full bg-muted rounded-xl"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
